import type { App, DefineComponent, Component, ComponentPublicInstance, Plugin } from 'vue';
import type { EConnectElementOptions } from './eConnectElementOptions';
import type { SupportedChildTypes } from './supportedChildTypes';

import { defineCustomElement, defineSSRCustomElement } from 'vue';
import { addGlobalStylesToShadowRoot } from './globalStyles';
import { useLogger } from 'composables/useLogger';
import { globalPinia } from './globalPinia';
import { withFormSubmitter } from './withFormSubmitter';

/**
 * Accept anything Vue considers a valid component for `defineCustomElement`.
 */
declare type Comp =
  | DefineComponent<any, any, any, any, any, any, any, any, any, any>
  | Component<any, any, any>;

const { logger } = useLogger('defineEConnectElement', {
  level: 'debug'
});

/** helpers */
function camelToKebab(s: string) {
  return s.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
function kebabToCamel(s: string) {
  return s.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}
function tryParseDataJSON(raw?: string): unknown {
  if (raw == null) return undefined;
  const t = String(raw).trim();
  if (!t) return undefined;
  try {
    return JSON.parse(t);
  } catch {
    // Be forgiving about single quotes for objects/arrays
    if (/^[[{]/.test(t)) {
      try {
        return JSON.parse(t.replace(/'/g, '"'));
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
}

export function defineEConnectElement(
  component: Comp,
  elementOptions?: EConnectElementOptions
): CustomElementConstructor {
  const EventHandlers: Record<string, (e: CustomEvent) => void> = {};

  const pinia = globalPinia;

  const configApp: (app: App) => void = app => {
    app.use(pinia as Plugin);

    app.config.errorHandler = (err: unknown, _: ComponentPublicInstance | null, info: string) => {
      logger.error(info, err);
    };
  };

  const customOptions = elementOptions?.customElementOptions ?? { shadowRoot: true };

  const element = elementOptions?.ssr
    ? defineSSRCustomElement(component, {
        configureApp: configApp,
        ...customOptions
      })
    : defineCustomElement(component, {
        configureApp: configApp,
        ...customOptions
      });

  if (elementOptions?.formAssociated) {
    (element as any).formAssociated = true;
  }

  const attributes = component?.props ? Object.keys(component?.props) : [];
  const hasModelValueProp = !!component?.props && 'modelValue' in component.props;

  // Build list of data-* attributes derived from declared props (e.g. items -> data-items)
  const dataAttrNames = (attributes ?? []).map(a => `data-${camelToKebab(a)}`);

  const base = elementOptions?.formSubmitter ? withFormSubmitter(element) : element;
  const defaultTrim = elementOptions?.trimDefaultSlotWhitespace ?? true;
  class EConnectBase extends base {
    [key: string]: any;

    registeredChildren: Record<string, SupportedChildTypes[]> = {};

    static get observedAttributes() {
      // also watch value/model-value so we can normalize them
      const extra = hasModelValueProp ? ['value', 'model-value'] : [];
      // include attributeEventMapping keys so attributeChangedCallback runs for handler attributes (e.g., on-update)
      const mapped = Object.keys(elementOptions?.attributeEventMapping ?? {});
      return Array.from(new Set([...(attributes ?? []), ...extra, ...dataAttrNames, ...mapped]));
    }

    /** Per-class opt-out: set `static trimDefaultSlotWhitespace = false` in your component class if needed. */
    static readonly trimDefaultSlotWhitespace = defaultTrim;

    /** Per-instance opt-in/out via attribute: data-trim-default-slot="true|false" */
    get _shouldTrimDefaultSlot(): boolean {
      // Per-instance attribute wins
      const a = this.getAttribute('data-trim-default-slot');
      if (a != null) return a !== 'false';
      // Then per-class static
      const ctor = this.constructor as typeof EConnectBase;
      return ctor.trimDefaultSlotWhitespace;
    }

    #mo?: MutationObserver;
    #cleanupSlotListeners: Array<() => void> = [];

    constructor() {
      super();
    }

    /**
     *  Called each time the element is added to the document.
     *  The specification recommends that, as far as possible, developers should implement custom element setup in this callback rather than the constructor.
     */
    connectedCallback() {
      // Pre-parse data-* JSON to props before Vue mounts
      try {
        const propsSet = new Set(attributes ?? []);
        const ds = (this as HTMLElement).dataset ?? {};
        for (const [key, raw] of Object.entries(ds)) {
          // dataset uses camelCase keys, e.g. data-items -> items
          if (!propsSet.has(key)) continue;
          const parsed = tryParseDataJSON(raw);
          if (parsed !== undefined) {
            (this as any)[key] = parsed;
          }
        }
      } catch (e) {
        logger.debug('data-* pre-parse failed', e);
      }

      // Normalize initial value before Vue mounts
      if (hasModelValueProp) {
        const mvProp = (this as any).modelValue;
        const mvAttr = this.getAttribute('model-value');
        const vAttr = this.getAttribute('value');

        if (mvProp == null) {
          if (mvAttr != null) {
            // already provided as attribute -> leave as-is so Vue reads it
          } else if (vAttr != null) {
            // Set model-value attribute so Vue CE initializes the prop from attribute
            this.setAttribute('model-value', vAttr);
            // prevent $attrs value from overriding inner input while typing
            this.removeAttribute('value');
          }
        } else if (vAttr != null) {
          // even if prop already set, make sure host value attr can't fight it
          this.removeAttribute('value');
        }
      }

      // Vue mounts here
      super.connectedCallback();

      // Wait for Vue to attach the shadowRoot & slots
      queueMicrotask(() => this.#initDefaultSlotWhitespaceTrim());

      addGlobalStylesToShadowRoot(this.shadowRoot);
    }

    /**
     * Called each time the element is removed from the document.
     */
    disconnectedCallback() {
      this.#teardownDefaultSlotWhitespaceTrim();
      super.disconnectedCallback();
    }

    /**
     * Called when attributes are changed, added, removed, or replaced.
     * See Responding to attribute changes for more details about this callback.
     * https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements#responding_to_attribute_changes
     */
    attributeChangedCallback(name: string, oldValue?: string, newValue?: string) {
      if (oldValue === newValue) return;

      this.#handleDataAttributeChange(name, newValue);
      this.#handleModelValueChange(name, newValue);
      this.#handleAttributeEventMapping(name, newValue);

      // If we just handled a 'value' attribute for modelValue normalization we
      // convert it to 'model-value' ourselves. Do NOT forward 'value' to the
      // Vue custom element base (it could reset modelValue to the old value).
      const skipSuperForward = hasModelValueProp && name === 'value';

      if (!skipSuperForward) {
        const superProto = Object.getPrototypeOf(EConnectBase.prototype) as {
          attributeChangedCallback?: (n: string, o?: string, v?: string) => void;
        };
        const superCb = superProto?.attributeChangedCallback;
        if (typeof superCb === 'function') {
          try {
            superCb.call(this, name, oldValue, newValue);
          } catch {
            /* swallow */
          }
        }
      }
    }

    #handleDataAttributeChange(name: string, newValue?: string): boolean {
      if (!name.startsWith('data-')) return false;
      const propName = kebabToCamel(name.slice(5));
      if (!(attributes ?? []).includes(propName)) return false;
      const parsed = typeof newValue === 'string' ? tryParseDataJSON(newValue) : undefined;
      if (parsed !== undefined) {
        (this as any)[propName] = parsed;
        return true;
      }
      return false;
    }

    #handleModelValueChange(name: string, newValue?: string): boolean {
      if (!hasModelValueProp) return false;

      if (name === 'model-value') {
        (this as any).modelValue = newValue;
        return true;
      }

      if (name === 'value') {
        // Empty value => just clean up
        if (newValue == null) {
          queueMicrotask(() => this.removeAttribute('value'));
          return true;
        }

        const hasPreset = (this as any).modelValue != null;

        // Pre-connect phase (element created but not yet connected to DOM)
        if (!this.isConnected) {
          if (hasPreset) {
            // User explicitly set a modelValue before the attribute; do NOT override.
            queueMicrotask(() => this.removeAttribute('value'));
            return true;
          }
          // No preset: drive initialization via attribute so Vue CE picks it up during mount.
          if (this.getAttribute('model-value') !== newValue) {
            this.setAttribute('model-value', newValue);
          }
          queueMicrotask(() => this.removeAttribute('value'));
          return true;
        }

        // Connected: per spec/test we always let a new value override existing modelValue.
        (this as any).modelValue = newValue;
        queueMicrotask(() => this.removeAttribute('value'));
        return true;
      }
      return false;
    }

    #handleAttributeEventMapping(name: string, newValue?: string): void {
      const mapping = elementOptions?.attributeEventMapping;
      if (!mapping?.[name]) return;
      if (typeof newValue !== 'string') return;
      EventHandlers[name] =
        typeof window[newValue as any] === 'function'
          ? (window as any)[newValue]
          : new Function('e', newValue);
    }

    #initDefaultSlotWhitespaceTrim(): void {
      if (!this._shouldTrimDefaultSlot) return;
      const sr = this.shadowRoot;
      if (!sr) return;

      const defaultSlots = Array.from<HTMLSlotElement>(sr.querySelectorAll('slot:not([name])'));
      if (defaultSlots.length === 0) return;

      const prune = () => {
        // Only act if the default slot is "assigned" **only** by whitespace Text nodes.
        // If any element is assigned, we leave everything as-is.
        const assignedNodes = defaultSlots.flatMap(s => s.assignedNodes({ flatten: true }));
        const hasAssignedElement = assignedNodes.some(n => n.nodeType === Node.ELEMENT_NODE);
        if (hasAssignedElement) return;

        // Remove *only* whitespace-only Text nodes among the host's immediate children.
        // (We don't touch meaningful text.)
        const toRemove: ChildNode[] = [];
        for (const n of Array.from(this.childNodes)) {
          if (n.nodeType === Node.TEXT_NODE && !n.textContent?.trim()) {
            toRemove.push(n);
          }
        }
        toRemove.forEach(n => {
          // Must remove from the host to truly empty the default slot
          if (n.parentNode === this) this.removeChild(n);
        });
      };

      // Initial pass
      prune();

      // React to slot reassignments
      for (const s of defaultSlots) {
        const handler = () => prune();
        s.addEventListener('slotchange', handler);
        this.#cleanupSlotListeners.push(() => s.removeEventListener('slotchange', handler));
      }

      // React to host light-DOM changes (Razor re-renders, etc.)
      this.#mo = new MutationObserver(prune);
      this.#mo.observe(this, { childList: true, characterData: true });
    }

    #teardownDefaultSlotWhitespaceTrim(): void {
      this.#mo?.disconnect();
      this.#mo = undefined;
      this.#cleanupSlotListeners.forEach(off => off());
      this.#cleanupSlotListeners = [];
    }

    /**
     * Map custom event handlers to the element.
     * This is used to map events to attributes defined in the element options.
     * For example: <my-element my-event="handleMyEvent"></my-element>
     */
    mapCustomEventHandlers() {
      Object.entries(elementOptions?.attributeEventMapping ?? {}).forEach(m => {
        m[1].forEach(eventName => {
          this.addEventListener(eventName, this.handleEvent);
        });
      });
    }

    /**
     * Handle custom events that are mapped to attributes.
     * This is used to handle events that are defined in the element options.
     * For example: <my-element my-event="handleMyEvent"></my-element>
     */
    handleEvent(e: Event) {
      if (!(e instanceof CustomEvent)) return;

      logger.info('handleEvent', e);

      Object.entries(elementOptions?.attributeEventMapping ?? {}).forEach(
        ([attributeName, events]) => {
          if (events.includes(e.type) && EventHandlers[attributeName])
            EventHandlers[attributeName](e);
        }
      );
    }

    /**
     * Slot content of custom elements is not loaded into the shadow DOM by default.
     * To support reusing styles within the shadow DOM within the slots we enable the use templates.
     * Templates are loaded but not rendered by default, therefore template content is cloned and injected into to the associated slots.
     * For example: <template slot="content"><span>foo</span></template>
     */
    loadTemplatesIntoSlots() {
      if (!this.shadowRoot) return;

      const slots = this.shadowRoot.querySelectorAll('slot');
      const templates = [...this.querySelectorAll('template')];

      logger.debug('loadTemplatesIntoSlots', {
        name: this.localName,
        slots: slots.length,
        templates: templates.length
      });

      slots.forEach(slot => {
        const template = templates.find(t => t.slot == slot.name);

        logger.debug(slot.name, {
          name: this.localName,
          slot: slot.name,
          hasTemplate: template !== undefined
        });

        if (template) {
          const children = [...slot.children];

          children.forEach(child => {
            logger.debug(child.localName, {
              name: this.localName,
              slot: slot.name,
              removeChild: child.innerHTML
            });
            slot.removeChild(child);
          });

          const clone = document.importNode(template.content, true);

          logger.debug('clone', { name: this.localName, slot: slot.name, clone: clone });

          slot.append(clone);
        }
      });

      templates.forEach(t => {
        logger.debug('removeTemplate', { name: this.localName, removeTemplate: t.slot });

        this.removeChild(t);
      });
    }

    /**
     * Register child elements to the parent element.
     * This is used to register child elements that are defined in the element options.
     * For example: <my-element><my-child></my-child></my-element>
     */
    registerChild(element: Element) {
      const childOptions = elementOptions?.childElementOptions?.[element.localName];
      const propertyNames = childOptions?.props;
      const attrs = [...element.attributes];

      propertyNames?.forEach(({ name, target }) => {
        if (target != this.localName) return;

        // Create model to push to parent prop
        const childModel = {} as SupportedChildTypes;
        attrs.forEach(attr => {
          childModel[attr.name] = attr.value;
        });

        this.registeredChildren[name] ??= [];
        this.registeredChildren[name].push(childModel);
      });
    }

    /**
     * Update the properties of the element based on the slots and child elements.
     * This is used to update the properties of the element based on the child elements that are registered.
     * For example: <my-element><my-child></my-child></my-element>
     */
    updateProps() {
      const slots = this.shadowRoot?.querySelectorAll('slot');

      if (!slots) return;

      const childElements = Object.keys(elementOptions?.childElementOptions ?? {});

      slots.forEach(slot => this.processSlot(slot, childElements));

      this.applyRegisteredChildrenToProps();
    }

    /**
     * Iterate over the children of an element and perform an action on each child.
     * @param element The element to iterate over.
     * @param action The action to perform on each child element.
     */
    iterateChildren(element: Element, action: (element: Element) => void) {
      action(element);

      if (!element.children) return;

      const children = [...element.children];
      children.forEach(e => {
        this.iterateChildren(e, action);
      });
    }

    /**
     * Process the slot element and register child elements.
     * This is used to process the slot element and register child elements that are defined in the element options.
     */
    private processSlot(slot: HTMLSlotElement, childElements: string[]) {
      logger.debug('processSlot', { name: this.localName, slot: slot.name });

      const options = elementOptions?.childElementOptions;
      if (!options) return;

      this.iterateChildren(slot, element => {
        if (!childElements.includes(element.localName)) return;

        this.registerElementEvents(element, options);
        this.registerChild(element);

        if (element.localName === this.localName) {
          this.clearSlotChildren(slot);
        }
      });
    }

    /**
     * Register events for child elements based on the element options.
     * This is used to register events for child elements that are defined in the element options.
     */
    private registerElementEvents(
      element: Element,
      options: EConnectElementOptions['childElementOptions']
    ) {
      const elementOptions = options?.[element.localName];
      if (!elementOptions) return;

      elementOptions.events.forEach(eventType => {
        element.addEventListener(eventType, this.handleEvent);
      });
    }

    /**
     * Apply the registered children to the properties of the element.
     * This is used to apply the registered children to the properties of the element.
     */
    private applyRegisteredChildrenToProps() {
      const mappedPropNames = Object.keys(this.registeredChildren);
      logger.debug('applyRegisteredChildrenToProps', {
        name: this.localName,
        props: mappedPropNames
      });

      mappedPropNames.forEach(name => {
        const items = this.registeredChildren[name] ?? [];
        if (items.length > 0) {
          this[name] = items;
        }
      });
    }

    /**
     * Clear the children of a slot element.
     * This is used to clear the children of a slot element before rendering new content.
     */
    private clearSlotChildren(slot: HTMLSlotElement) {
      Array.from(slot.children).forEach(child => {
        slot.removeChild(child);
      });
    }
  }

  return EConnectBase as unknown as CustomElementConstructor;
}
